"use client";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { type ReactNode } from "react";
import type { BeforeSendFn, CaptureResult } from "posthog-js";
import { authService } from "@/lib/authService";
import { shouldDisablePostHogFeatureFlags } from "./posthogFeatureFlags";

const getPostHogPageviewContext = () => {
  if (typeof document === "undefined") {
    return {};
  }

  const element = document.querySelector<HTMLElement>(
    "[data-posthog-pageview]",
  );
  if (!element) {
    return {};
  }

  const {
    posthogPageName,
    posthogPageType,
    posthogCreatorId,
    posthogCreatorUsername,
    posthogUsername,
    posthogJobId,
    posthogJobTitle,
    posthogJobCompany,
    posthogJobValue,
    posthogRevenueType,
    posthogPulseVariant,
  } = element.dataset;

  const pathname = window.location.pathname.toLowerCase();
  const isProfilePath =
    /^\/@[^/]+\/?$/.test(pathname) || /^\/s@[^/]+\/?$/.test(pathname);
  const isJobDetailsPath =
    pathname.includes("/jobs/") &&
    (pathname.startsWith("/@") || pathname.startsWith("/s@"));

  if (
    (posthogPageType === "creator_profile" && !isProfilePath) ||
    (posthogPageType === "creator_job_details" && !isJobDetailsPath)
  ) {
    return {};
  }

  const context: Record<string, string | number> = {};

  if (posthogPageName) context.page_name = posthogPageName;
  if (posthogPageType) context.page_type = posthogPageType;
  if (posthogCreatorId) context.creator_id = posthogCreatorId;
  if (posthogCreatorUsername) context.creator_username = posthogCreatorUsername;
  if (posthogUsername) context.username = posthogUsername;
  if (posthogJobId) context.job_id = posthogJobId;
  if (posthogJobTitle) context.job_title = posthogJobTitle;
  if (posthogJobCompany) context.job_company = posthogJobCompany;
  if (posthogRevenueType) context.revenue_type = posthogRevenueType;
  if (posthogPulseVariant) context.pulse_variant = posthogPulseVariant;
  if (posthogJobValue) {
    const parsedJobValue = Number(posthogJobValue);
    context.job_value = Number.isFinite(parsedJobValue)
      ? parsedJobValue
      : posthogJobValue;
  }

  return context;
};

// Initialize PostHog at module level so it's ready before any component renders.
// This avoids a race condition where events fired in early useEffects
// (e.g. CR_SIGNUP on connect-account) were silently dropped.
if (typeof window !== "undefined") {
  const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;
  const uiHost = process.env.NEXT_PUBLIC_POSTHOG_UI_HOST;

  if (apiKey && !posthog.__loaded) {
    const currentPath = window.location.pathname;
    const isAuthenticated = authService.isAuthenticated();
    const disableFlags = shouldDisablePostHogFeatureFlags(
      currentPath,
      isAuthenticated,
    );

    const beforeSend: BeforeSendFn = (event: CaptureResult | null) => {
      if (!event || event.event !== "$pageview") {
        return event;
      }

      const pageviewContext = getPostHogPageviewContext();
      if (Object.keys(pageviewContext).length === 0) {
        return event;
      }

      return {
        ...event,
        properties: {
          ...event.properties,
          ...pageviewContext,
        },
      };
    };

    posthog.init(apiKey, {
      api_host: host || "https://app.posthog.com",
      ui_host: uiHost || "https://us.posthog.com",
      capture_pageleave: true,
      autocapture: false,
      person_profiles: "identified_only",
      persistence: "localStorage+cookie",
      advanced_disable_feature_flags: disableFlags,
      advanced_disable_flags: disableFlags,
      before_send: beforeSend,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "[data-sensitive]",
      },
      loaded: (ph) => {
        if (process.env.NODE_ENV === "development") {
          ph.debug();
        }
      },
    });
  }
}

export function PostHogProvider({ children }: { children: ReactNode }) {
  return <PHProvider client={posthog}>{children}</PHProvider>;
}
